import type { Experiment } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import { isSocialProofPopupSuppressed, recordSocialProofPopupExposure } from 'storage/socialProofPopup'

const SHOW_DELAY_MS = 1500
const SOCIAL_PROOF_DURATION_MS = 8000
const MAX_AGE_HOURS = 72

export interface SocialProofPopupDialog {
  message: string
  offerImage: App.Image
}

interface RecentPurchaseSocialProof {
  isWithinMaxAge: boolean
  purchaseCountry?: string
  purchasedAt?: string
  purchaseKey?: string
}

interface TimerRef {
  current: number | undefined
}

export const SOCIAL_PROOF_COPY_VARIANTS = {
  flashHotel: 'flashHotel',
  sppStay: 'sppStay',
} as const

export type SocialProofCopyVariant =
  (typeof SOCIAL_PROOF_COPY_VARIANTS)[keyof typeof SOCIAL_PROOF_COPY_VARIANTS]

interface UseSocialProofPopupOptions {
  copyVariant: SocialProofCopyVariant
  experiment: Experiment
  offerImage: App.Image
  recentPurchases: Array<App.OfferRecentPurchase> | undefined
}

function getPurchaseKey(purchaseCountry: string | undefined, purchasedAt: string | undefined) {
  return purchaseCountry && purchasedAt ? `${purchaseCountry}:${purchasedAt}` : undefined
}

function isRecentPurchaseWithinMaxAge(purchasedAt: string | undefined) {
  if (!purchasedAt) return false

  return moment().diff(moment(purchasedAt), 'hours') < MAX_AGE_HOURS
}

function getRecentPurchaseSocialProof(recentPurchases: Array<App.OfferRecentPurchase> | undefined): RecentPurchaseSocialProof {
  const recentPurchase = recentPurchases?.[0]
  const purchaseCountry = recentPurchase?.country
  const purchasedAt = recentPurchase?.purchasedAt

  return {
    isWithinMaxAge: isRecentPurchaseWithinMaxAge(purchasedAt),
    purchaseCountry,
    purchasedAt,
    purchaseKey: getPurchaseKey(purchaseCountry, purchasedAt),
  }
}

function canActivateRecentPurchaseSocialProof({ isWithinMaxAge, purchaseCountry, purchasedAt, purchaseKey }: RecentPurchaseSocialProof) {
  return !!purchaseKey && !!purchaseCountry && !!purchasedAt && isWithinMaxAge
}

function getCountryName(locale: string, countryCode: string) {
  if (typeof Intl.DisplayNames !== 'function') {
    return countryCode
  }

  try {
    const countryNames = new Intl.DisplayNames([locale], { type: 'region' })

    return countryNames.of(countryCode) ?? countryCode
  } catch {
    return countryCode
  }
}

function formatSocialProofMessage(intl: IntlShape, purchaseCountry: string, purchasedAt: string, copyVariant: SocialProofCopyVariant) {
  const countryName = getCountryName(intl.locale, purchaseCountry)
  const timeAgo = moment(purchasedAt).fromNow()

  if (copyVariant === SOCIAL_PROOF_COPY_VARIANTS.sppStay) {
    return intl.formatMessage(
      {
        id: 'nFUqkv0W5BLYIWhAoIMk',
        defaultMessage: 'Someone from {countryName} booked this stay {timeAgo}.',
        description: 'Social proof popup message on SPP accommodation property pages showing where and when a recent stay booking happened.',
      },
      { countryName, timeAgo },
    )
  }

  return intl.formatMessage(
    {
      id: 'hsJIsh7AXvZkocH0b0P1',
      defaultMessage: 'Someone from {countryName} purchased this hotel {timeAgo}.',
      description: 'Social proof popup message on accommodation offer pages showing where and when a recent booking happened.',
    },
    { countryName, timeAgo },
  )
}

function clearDismissTimer(dismissTimerRef: TimerRef) {
  window.clearTimeout(dismissTimerRef.current)
  dismissTimerRef.current = undefined
}

function scheduleDismissTimer(dismissTimerRef: TimerRef, dismissDialog: () => void) {
  clearDismissTimer(dismissTimerRef)
  dismissTimerRef.current = window.setTimeout(() => {
    dismissTimerRef.current = undefined
    dismissDialog()
  }, SOCIAL_PROOF_DURATION_MS)
}

function useSocialProofPopup({
  copyVariant,
  experiment,
  offerImage,
  recentPurchases,
}: UseSocialProofPopupOptions) {
  const intl = useIntl()
  const offerImageRef = useRef(offerImage)
  const displayedPurchaseKeyRef = useRef<string | undefined>(undefined)
  const dismissTimerRef = useRef<number | undefined>(undefined)
  const [dialog, setDialog] = useState<SocialProofPopupDialog | undefined>(undefined)
  const recentPurchaseSocialProof = useMemo(() => getRecentPurchaseSocialProof(recentPurchases), [recentPurchases])
  const {
    purchaseCountry,
    purchasedAt,
    purchaseKey,
  } = recentPurchaseSocialProof
  const [isSuppressed, setIsSuppressed] = useState(true)
  const canActivateSocialProof = canActivateRecentPurchaseSocialProof(recentPurchaseSocialProof) && !isSuppressed
  const {
    isTurnedOn: isSocialProofTurnedOn,
    isAnyVariant: isSocialProofEnabled,
  } = useExperiment(experiment, { canActivate: canActivateSocialProof })

  useEffect(() => {
    offerImageRef.current = offerImage
  }, [offerImage])

  const hideDialog = useCallback(() => {
    setDialog(undefined)
  }, [])

  const dismissDialog = useCallback(() => {
    clearDismissTimer(dismissTimerRef)
    hideDialog()
  }, [hideDialog])

  useEffect(() => {
    return () => {
      clearDismissTimer(dismissTimerRef)
    }
  }, [])

  useEffect(() => {
    setIsSuppressed(!purchaseKey || isSocialProofPopupSuppressed())
  }, [purchaseKey])

  useEffect(() => {
    if (!canActivateSocialProof || !isSocialProofTurnedOn || !purchaseKey || !purchaseCountry || !purchasedAt || displayedPurchaseKeyRef.current === purchaseKey) return

    const timer = window.setTimeout(() => {
      if (isSocialProofPopupSuppressed()) {
        setIsSuppressed(true)
        return
      }

      displayedPurchaseKeyRef.current = purchaseKey
      recordSocialProofPopupExposure()
      setIsSuppressed(true)

      if (!isSocialProofEnabled) return

      setDialog({
        message: formatSocialProofMessage(intl, purchaseCountry, purchasedAt, copyVariant),
        offerImage: offerImageRef.current,
      })
      scheduleDismissTimer(dismissTimerRef, hideDialog)
    }, SHOW_DELAY_MS)

    return () => window.clearTimeout(timer)
  }, [canActivateSocialProof, copyVariant, hideDialog, intl, isSocialProofEnabled, isSocialProofTurnedOn, purchaseCountry, purchaseKey, purchasedAt])

  return useMemo(() => ({
    dialog,
    dismissDialog,
  }), [dialog, dismissDialog])
}

export default useSocialProofPopup
